import { Stack, useRouter, usePathname } from 'expo-router';
import * as SplashScreen from 'expo-splash-screen';
import { SQLiteProvider, useSQLiteContext } from 'expo-sqlite';
import { useEffect, useState } from 'react';
import { StatusBar } from 'expo-status-bar';
import { KeyboardAvoidingView, Platform, Pressable, StyleSheet, Text, View } from 'react-native';

import {
  Fraunces_500Medium,
  Fraunces_500Medium_Italic,
  Fraunces_600SemiBold,
  useFonts as useFraunces,
} from '@expo-google-fonts/fraunces';
import {
  HankenGrotesk_400Regular,
  HankenGrotesk_500Medium,
  HankenGrotesk_600SemiBold,
  HankenGrotesk_700Bold,
  useFonts as useHanken,
} from '@expo-google-fonts/hanken-grotesk';

import Animated, {
  Easing,
  runOnJS,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';

import { AppErrorBoundary } from '@/components/AppErrorBoundary';
import { ConfettiProvider } from '@/components/ConfettiOverlay';
import * as QuickActions from 'expo-quick-actions';
import { useQuickActionRouting } from 'expo-quick-actions/router';
import { useShareIntent } from 'expo-share-intent';

import { sharedFilesCache } from '@/lib/smart-scan';

import { ConfirmDialogProvider } from '@/components/ConfirmDialog';
import { UndoToastHost } from '@/components/UndoToastHost';
import { DesktopSidebar } from '@/components/desktop/DesktopSidebar';
import { Preloader } from '@/components/Preloader';
import { Fonts, Palette } from '@/constants/theme';
import { isBootReady, markBootReady, useBootReady } from '@/lib/boot-ready';
import { DATABASE_NAME, migrateWithRetry } from '@/db/schema';
import { useIsDesktop } from '@/hooks/use-is-desktop';
import { useAchievementWatcher } from '@/lib/achievement-watcher';
import { autoApplyUpdateOnLaunch } from '@/lib/app-updates';
import { useMessagesInit } from '@/lib/messages';
import { useAndroidBackGuard } from '@/lib/android-back';
import { ensureWebHoverCss } from '@/lib/web-hover-css';
import { useOnboardingGate } from '@/lib/onboarding-gate';
import { useDataRevision } from '@/lib/data-revision';
import { setActiveProperty } from '@/lib/tasks';
import { dismissResolvedWeeklyPrompts, maybePostWeeklyPrompt } from '@/lib/weekly-prompts';
import {
  ensureAndroidNotificationChannel,
  ensureServiceWorkerRegistered,
  fireDueWebReminders,
} from '@/lib/notifications';
import * as Notifications from 'expo-notifications';
import { composeAndScheduleDigest } from '@/lib/notifications';
import {
  installHolderYieldHandler,
  requestTakeoverAndReload,
  scheduleTakeoverOnFocus,
  wasJustYielded,
} from '@/lib/tab-lock';

SplashScreen.preventAutoHideAsync().catch(() => {
  // Already hidden — fine.
});

const APP_MAX_WIDTH = 440;

/**
 * URLs that belong to the onboarding linear flow. Kept as a literal set
 * because the `(onboarding)` route group doesn't appear in the URL and
 * useSegments() can return empty on the first render — pathname is the
 * reliable signal. Update this if new onboarding screens land.
 */
const ONBOARDING_PATHS = new Set([
  '/start',
  '/welcome',
  '/path',
  '/home-type',
  '/year',
  '/size',
  '/hvac',
  '/inside',
  '/property',
  '/lifestyle',
  '/detail',
  '/generating',
  '/plan',
]);

export default function RootLayout() {
  // If this tab yielded the OPFS lock in a previous boot (within the TTL),
  // start it in the standby state directly — do NOT mount SQLiteProvider,
  // which would race the new holder for the lock and reignite the
  // ping-pong loop. The user explicitly bringing focus back to this tab
  // (or clicking "Move it here") clears the flag in requestTakeoverAndReload
  // and the next boot will acquire the lock cleanly.
  const [dbError, setDbError] = useState<Error | null>(() => {
    if (Platform.OS !== 'web') return null;
    if (!wasJustYielded()) return null;
    const syntheticError = new Error(
      'NoModificationAllowedError: Another Homestead tab is currently using your data. This tab is in standby.'
    );
    return syntheticError;
  });
  const isDesktop = useIsDesktop();
  // Onboarding lives under the `(onboarding)` route group. We render it
  // in the mobile-column shell on every viewport — there's no nav rail to
  // show (the user hasn't picked tabs yet) and no glance data to fill the
  // right rail with. Onboarding is a focused linear flow; full-bleed
  // centered column is the right pattern on phone AND on desktop.
  //
  // Detection: pathname-based against the known onboarding URLs because
  // useSegments() can return [] on the very first render before Expo
  // Router resolves the route — that race showed the desktop shell on
  // /generating, which then left the main column blank because the
  // generating screen expected a full-bleed canvas.
  const pathname = usePathname();
  const inOnboarding = ONBOARDING_PATHS.has(pathname);
  const useDesktopShell = isDesktop && !inOnboarding;
  const [fraunces, fraunceErr] = useFraunces({
    Fraunces_500Medium,
    Fraunces_500Medium_Italic,
    Fraunces_600SemiBold,
  });
  const [hanken, hankenErr] = useHanken({
    HankenGrotesk_400Regular,
    HankenGrotesk_500Medium,
    HankenGrotesk_600SemiBold,
    HankenGrotesk_700Bold,
  });

  const fontsReady = (fraunces && hanken) || fraunceErr || hankenErr;

  // Hide the OS splash as soon as JS mounts — NOT after fonts load. The
  // animated Preloader uses the same cream background and house icon, so
  // the OS-splash → Preloader handoff is invisible. Waiting for fonts
  // creates a visible "splash → blank → preloader" sequence on mobile
  // that reads as two separate loading screens to the user.
  useEffect(() => {
    SplashScreen.hideAsync().catch(() => {});
  }, []);

  // Register the web service worker once at boot. Fire-and-forget — failure
  // just means web notifications fall back to inline `new Notification(...)`.
  useEffect(() => {
    void ensureServiceWorkerRegistered();
  }, []);

  // Android 8+ silently drops notifications without a registered channel.
  // Set it up at boot so every scheduling path lands cleanly. No-op on
  // iOS / web; idempotent so it's safe to call repeatedly.
  useEffect(() => {
    void ensureAndroidNotificationChannel();
  }, []);


  // Browser tab title. Expo's static export emits a <title> tag from
  // +html.tsx, but react-helmet-async (used by expo-router) renders an
  // empty <title data-rh="true"/> that wins on hydration — leaving the
  // tab showing the URL hostname (app.homestead.ninja) instead of our
  // brand. Setting document.title imperatively on web after mount
  // overrides Helmet's empty state. No-op on native.
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    if (typeof document === 'undefined') return;
    document.title = 'Homestead — Your home maintenance plan, auto-generated.';
  }, []);

  // Seeding the inbox is now handled by `useMessagesInit` inside
  // <AppGlobals/> (a child of SQLiteProvider) — it needs the DB, which
  // isn't yet available here at the root.

  // Safety net: if the app hasn't reached "boot ready" within 15s AND
  // hasn't surfaced a dbError, force a dbError. Catches edge cases where
  // SQLiteProvider hangs without firing onError (notably some
  // incognito/private-mode browsers where OPFS init doesn't resolve)
  // and prevents the user from staring at an infinite preloader.
  useEffect(() => {
    if (dbError) return;
    const id = setTimeout(() => {
      if (isBootReady()) return;
      setDbError(
        new Error(
          'Database initialization timed out. Try reloading. ' +
            'Incognito / private browsing can restrict the storage Homestead needs.'
        )
      );
    }, 15_000);
    return () => clearTimeout(id);
  }, [dbError]);

  // Silent auto-update on launch: check for a newer JS bundle, download it,
  // and reload — no force-close required. Runs once per app boot, after the
  // UI has had a moment to render so the user sees the splash → home flow
  // rather than a blank frame. Failures are swallowed (the user can still
  // hit Settings → Check for updates manually).
  useEffect(() => {
    void autoApplyUpdateOnLaunch();
  }, []);

  // Multi-tab coordination — once this tab boots, broadcast "I have the
  // DB lock" and listen for takeover requests from sibling tabs. If a
  // sibling asks for the lock, we yield by reloading (which releases the
  // OPFS handle on unload). Web-only; no-op on iOS / Android. We mount
  // this from the success path because installing the listener BEFORE we
  // know whether we have the lock would mean two locked-out tabs ping-
  // ponging takeover requests at each other forever.
  useEffect(() => {
    if (dbError) return;
    installHolderYieldHandler();
  }, [dbError]);

  // Scroll-restore reset: on each in-app navigation, force the window back
  // to the top so users don't land mid-page (browsers preserve scroll on
  // SPA route changes by default — fine for some apps, surprising here).
  // No-op outside web; native screens have their own scroll state per page.
  useScrollToTopOnRouteChange();

  // Don't return null while fonts load — React Native paints its default
  // white background and you get a one-frame flash between splash hide
  // and the first real render. Show the breathing-house Preloader (same
  // one the home tab uses during DB warmup) so web users see a friendly
  // animated brand-frame rather than a blank cream rectangle. Mobile
  // already gets the OS splash icon BEFORE this point so the handoff is
  // OS-splash → Preloader → home, which matches the user's described
  // intent ("homestead icon followed by the breathing house intro").
  if (dbError) return <DbErrorScreen error={dbError} onRetry={() => setDbError(null)} />;

  return (
    <AppErrorBoundary>
      <View style={{ flex: 1 }}>
      <SQLiteProvider
        databaseName={DATABASE_NAME}
        onInit={migrateWithRetry}
        onError={(err) => {
          console.warn('SQLite init failed', err);
          // Defer to avoid "Cannot update a component while rendering a
          // different component" — onError can fire inside the provider's
          // render commit. Microtask-deferred setState is safe.
          const next = err instanceof Error ? err : new Error(String(err));
          setTimeout(() => setDbError(next), 0);
        }}>
        <StatusBar style="dark" />
        <ConfettiProvider>
          <ConfirmDialogProvider>
            <View style={styles.outer}>
              <KeyboardAvoidingView
                style={[
                  styles.column,
                  // Main-app desktop: 880px column to leave room for
                  // the right-rail glance card while keeping a
                  // comfortable reading line.
                  useDesktopShell && { maxWidth: 880 },
                  // Onboarding desktop: still narrower than the main
                  // app (no right rail) but bumped to 560 instead of
                  // the mobile 440. The old 440 cap made /welcome and
                  // /home-type look like a tiny mobile-preview column
                  // on big monitors — user-visible "is this broken?"
                  // bug. Native + small viewport still get 440.
                  isDesktop && inOnboarding && { maxWidth: 560 },
                ]}
                // iOS pushes content above the keyboard so notes / form
                // fields near the bottom of long screens stay visible.
                // Android's native windowSoftInputMode handles resize.
                behavior={Platform.OS === 'ios' ? 'padding' : undefined}
                keyboardVerticalOffset={0}>
                <Stack
                  screenOptions={{
                    headerShown: false,
                    contentStyle: { backgroundColor: Palette.sand },
                    animation: 'fade',
                  }}
                />
              </KeyboardAvoidingView>
              {/* Floating right rail — position:fixed overlay anchored
                  to the viewport, NOT a layout sibling of the column.
                  Renders only on web+wide AND not in onboarding, AND
                  only when viewport has horizontal room (DesktopSidebar
                  enforces its own MIN_VIEWPORT). Lives at the same
                  level as the column so its absolute positioning is
                  isolated from Stack's internal layout work. */}
              {useDesktopShell ? <DesktopSidebar /> : null}
            </View>
            {/* Module-level effects that need DB access — mount as
                a single sibling so they share one mount lifecycle and
                live inside the SQLiteProvider tree. */}
            <AppGlobals />
            {/* Global Undo snackbar (Wave 48) — floats above the tab
                bar; any screen surfaces it via offerUndo(). */}
            <UndoToastHost />
            {/* Launcher-icon shortcuts (Wave 48) — native only, and the
                native module lands with the next eas build (not OTA). */}
            {Platform.OS !== 'web' ? <QuickActionsBridge /> : null}
            {/* Android share-sheet target (Wave 52) — files shared into
                Homestead run through the batch Smart Scan pipeline.
                Native module lands with the next eas build (not OTA);
                the hook no-ops gracefully until then. */}
            {Platform.OS !== 'web' ? <ShareIntentBridge /> : null}
          </ConfirmDialogProvider>
        </ConfettiProvider>
      </SQLiteProvider>
      {/* Single-instance Preloader overlay. Mounts on app boot, stays
          visible while fonts are downloading AND home data is being
          fetched, fades out the instant both are ready, then unmounts.
          Crucially this is ONE mount — the prior architecture used two
          separate Preloader mounts (font-load in _layout, DB-warmup in
          home tab) and the unmount/remount between them restarted
          every animation from frame zero, which read as visible jitter
          to the user. */}
      <PreloaderOverlay textReady={Boolean(fontsReady)} />
      </View>
    </AppErrorBoundary>
  );
}

/**
 * Boot-screen overlay. Manages its own opacity + mount lifecycle so the
 * Preloader animations never reset mid-boot.
 */
function PreloaderOverlay({ textReady }: { textReady: boolean }) {
  const bootReady = useBootReady();
  const ready = textReady && bootReady;
  const opacity = useSharedValue(1);
  const [mounted, setMounted] = useState(true);

  useEffect(() => {
    if (!ready) return;
    // Both signals are in — fade out, then unmount. The unmount step
    // is critical so pointer events on the now-revealed app aren't
    // intercepted by an invisible overlay.
    opacity.value = withTiming(
      0,
      { duration: 520, easing: Easing.inOut(Easing.quad) },
      (finished) => {
        if (finished) runOnJS(setMounted)(false);
      }
    );
  }, [ready, opacity]);

  const fadeStyle = useAnimatedStyle(() => ({ opacity: opacity.value }));

  if (!mounted) return null;
  return (
    <Animated.View
      pointerEvents={ready ? 'none' : 'auto'}
      style={[StyleSheet.absoluteFillObject, styles.overlay, fadeStyle]}>
      <Preloader textReady={textReady} />
    </Animated.View>
  );
}

/**
 * Renders nothing — exists purely as a mount point for hooks that
 * require DB access (SQLiteProvider must be in scope). The achievement
 * watcher needs both DB and data-revision; placing it here keeps the
 * RootLayout body uncluttered.
 */
/**
 * Launcher-icon shortcuts (Wave 48): long-press the Homestead icon on
 * the Android / iOS home screen → quick actions that deep-link straight
 * into the app. `useQuickActionRouting` turns a tapped action's
 * params.href into an expo-router navigation once the app is up.
 * Mounted native-only so the web bundle never touches the module.
 */
function QuickActionsBridge() {
  useQuickActionRouting();
  useEffect(() => {
    void QuickActions.setItems([
      {
        id: 'scan',
        title: 'Smart scan',
        subtitle: 'Snap any paper — auto-filed',
        icon: Platform.OS === 'ios' ? 'symbol:camera.viewfinder' : 'shortcut_scan',
        params: { href: '/scan' },
      },
      {
        id: 'expense',
        title: 'Log expense',
        icon: Platform.OS === 'ios' ? 'symbol:dollarsign.circle' : 'shortcut_expense',
        params: { href: '/expenses/new' },
      },
      {
        id: 'task',
        title: 'Add task',
        icon: Platform.OS === 'ios' ? 'symbol:plus.circle' : 'shortcut_task',
        params: { href: '/custom-task/none' },
      },
      {
        id: 'schedule',
        title: 'Open schedule',
        icon: Platform.OS === 'ios' ? 'symbol:calendar' : 'shortcut_schedule',
        params: { href: '/(tabs)/schedule' },
      },
    ]).catch((err) => console.warn('quick actions setItems failed', err));
  }, []);
  return null;
}

/**
 * Share-sheet intake (Wave 52): another app shares files (or a user
 * shares a PDF from Gmail/Drive) → Homestead appears in the Android
 * share sheet → shared files are stashed in sharedFilesCache and the
 * Smart Scan page consumes them, running its batch auto-categorize
 * pipeline. `useShareIntent` no-ops (with a console warning) on builds
 * that predate the native module, so this is OTA-safe.
 */
function ShareIntentBridge() {
  const router = useRouter();
  const { hasShareIntent, shareIntent, resetShareIntent } = useShareIntent();
  useEffect(() => {
    if (!hasShareIntent) return;
    const files = (shareIntent.files ?? [])
      .filter((f) => !!f.path)
      .map((f) => ({
        uri: f.path,
        name: f.fileName ?? null,
        mimeType: f.mimeType ?? null,
        size: f.size ?? null,
      }));
    resetShareIntent();
    if (files.length === 0) return;
    sharedFilesCache.value = files;
    router.push('/scan' as never);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasShareIntent]);
  return null;
}

function AppGlobals() {
  // Redirect deep links (shortcuts, share intake, notifications) to
  // onboarding when no home exists yet (Wave 58).
  useOnboardingGate();
  // Hardware back must never silently minimize mid-app (Wave 63).
  useAndroidBackGuard();
  // Web: one-time global hover-affordance stylesheet (Wave 63).
  ensureWebHoverCss();
  useAchievementWatcher();
  // Load persisted inbox from SQLite + run idempotent seed pass. Must
  // live inside SQLiteProvider so `useSQLiteContext` resolves; that's
  // exactly what AppGlobals is for.
  useMessagesInit();
  // Weekly "fill in one blank" prompt. Internally idempotent within an
  // ISO week, so calling on every app boot is safe — second-and-later
  // calls in the same week no-op via INSERT OR IGNORE on the seed key.
  useWeeklyTunePrompt();
  // Listen for taps on local push notifications so we can deep-link
  // into the right screen (e.g. the 24h post-onboarding tune nudge
  // routes to /profile-setup). Web no-op — handled inside the hook.
  useNotificationResponseRouter();
  // Web reminder delivery — scans task rows whose reminder_at has
  // passed but reminder_notified_at is still null, fires each via
  // service-worker showNotification, marks them notified. Native
  // already delivered the OS-scheduled notification so this is a
  // no-op there (the helper checks Platform.OS internally).
  useWebTaskReminders();
  // Multi-home aware daily digest. Recomputes the headline task on
  // each app boot so the OS-scheduled notification body always
  // reflects the most recent plan. Aggregates across every home,
  // not just whichever was last-active. Suppresses when no tasks
  // exist (no more "you have no tasks" buzz).
  useDigestRescheduler();
  // Signal the root layout that the app is ready to display. AppGlobals
  // mounts ONLY after SQLiteProvider successfully initializes (children
  // don't render until onInit resolves), so this is a robust signal
  // regardless of which route the user landed on — fixes the preloader
  // staying stuck when the user opens a deep link (e.g. /messages)
  // instead of the home tab. The home tab also calls markBootReady, but
  // its signal fires later (after useHomeData finishes); whichever fires
  // first wins, and the signal is idempotent.
  useEffect(() => {
    markBootReady();
  }, []);
  return null;
}

/**
 * Triggers the weekly tune-my-plan prompt on app boot. Lives here
 * (inside AppGlobals' subtree) so the SQLite context is available.
 * Module-scope idempotency in weekly-prompts.ts guarantees a single
 * post per ISO week even with hot reloads or rapid focus events.
 */
/**
 * Web-only: deliver task reminders whose fire time has passed since the
 * tab was last open. Native gets reminders via the OS scheduler so the
 * helper inside fireDueWebReminders short-circuits to a no-op there.
 *
 * Re-runs on data-revision changes so a freshly-set reminder during the
 * same session can still deliver if the user keeps the tab open past
 * the fire time.
 */
function useWebTaskReminders(): void {
  const db = useSQLiteContext();
  const revision = useDataRevision();
  useEffect(() => {
    let cancelled = false;
    const tick = () => {
      if (cancelled) return;
      void fireDueWebReminders(db).catch((err) => {
        console.warn('fireDueWebReminders failed', err);
      });
    };
    // Small defer to let the inbox/Greeting hydrate first, then check.
    const id = setTimeout(tick, 1500);
    return () => {
      cancelled = true;
      clearTimeout(id);
    };
  }, [db, revision]);
}

/**
 * Recomputes and reschedules the digest each app boot. OS scheduled
 * notifications can't dynamically query — they fire at the configured
 * time with whatever content was baked in at schedule time. So we
 * churn the schedule on every cold start (and on data-revision bumps,
 * with a debounce) to keep the headline fresh.
 *
 * The composer reads the active property's cadence preference
 * (off / daily / weekly / monthly + day + hour) itself, so the user's
 * chosen frequency is always respected. (The previous version of this
 * hook hardcoded a daily reschedule, which silently replaced a weekly
 * digest with a daily one on every app start.)
 */
function useDigestRescheduler(): void {
  const db = useSQLiteContext();
  const revision = useDataRevision();
  useEffect(() => {
    let cancelled = false;
    // Defer a touch so the initial hydration completes before we
    // start touching the OS scheduler. 2s mirrors the other boot
    // helpers in this file.
    const id = setTimeout(async () => {
      if (cancelled) return;
      try {
        await composeAndScheduleDigest(db);
      } catch (err) {
        console.warn('digest reschedule failed', err);
      }
    }, 2000);
    return () => {
      cancelled = true;
      clearTimeout(id);
    };
  }, [db, revision]);
}

function useWeeklyTunePrompt(): void {
  const db = useSQLiteContext();
  // Re-run the sweep whenever data revision bumps (e.g. the user just
  // picked a roof material in the walkthrough or edit screen). Without
  // this subscription the resolved nudge would linger in the inbox
  // until the next cold start, which feels broken — the user just
  // answered the question and the prompt should disappear.
  const revision = useDataRevision();
  useEffect(() => {
    let cancelled = false;
    // Slight defer — let the home tab settle before posting so a new
    // message doesn't pop into a still-loading inbox. 2s feels right
    // (after preloader fades + first paint completes).
    const id = setTimeout(() => {
      if (cancelled) return;
      // Sweep first so we don't immediately re-post about a gap the
      // user already filled this session, then offer a new prompt for
      // any remaining gap.
      void dismissResolvedWeeklyPrompts(db)
        .catch((err) => console.warn('dismissResolvedWeeklyPrompts failed', err))
        .then(() => maybePostWeeklyPrompt(db))
        .catch((err) => console.warn('weekly tune prompt failed', err));
    }, 2000);
    return () => {
      cancelled = true;
      clearTimeout(id);
    };
  }, [db, revision]);
}

/**
 * Listen for taps on local push notifications and route to whatever
 * URL the notification's data.route field specifies. This is how the
 * 24h post-onboarding nudge deep-links into /profile-setup — without
 * this listener, the tap would only open the app to wherever the user
 * left it.
 *
 * Convention: notification payloads use `data: { route: '/some/path' }`.
 * Any push that wants deep-linking just sets that field.
 *
 * Native only. Web notifications go through the service worker which
 * has its own click-handling story (separate from expo-notifications).
 */
function useNotificationResponseRouter(): void {
  const router = useRouter();
  const db = useSQLiteContext();
  useEffect(() => {
    if (Platform.OS === 'web') return;
    const sub = Notifications.addNotificationResponseReceivedListener((response) => {
      const data = response.notification.request.content.data as
        | { route?: string; propertyId?: string | null }
        | undefined;
      const route = data?.route;
      const propertyId = data?.propertyId;
      if (typeof route === 'string' && route.length > 0) {
        // Defer the navigation a tick so we don't race the root
        // layout's mount on a cold start (tap → app launches → this
        // listener fires before /(tabs) is even mounted).
        setTimeout(async () => {
          try {
            // Multi-home: if the notification was scheduled for a
            // task on a different home than the currently-active one,
            // switch active home FIRST. The DB write + bumpDataRevision
            // happen synchronously enough that the destination screen
            // mounts already showing the right home's data.
            if (typeof propertyId === 'string' && propertyId.length > 0) {
              await setActiveProperty(db, propertyId);
            }
            router.push(route as never);
          } catch {
            // Invalid route or DB failure — better to silently no-op
            // than crash the listener and break subsequent taps.
          }
        }, 100);
      }
    });
    return () => sub.remove();
  }, [router, db]);
}

function useScrollToTopOnRouteChange() {
  const pathname = usePathname();
  // Browser-native scroll restoration. On forward nav (router.push) the
  // browser starts the new page at top automatically; on back nav
  // (history.back) it restores the previous scroll position. That's
  // the standard "back returns me where I was" behavior the user asked
  // for.
  //
  // Earlier iteration set `scrollRestoration = 'manual'` and forced
  // scrollTo(0) on every pathname change — which broke back-nav by
  // overwriting the restored position. We've reverted that: leave
  // scrollRestoration on its default ('auto') and don't manually
  // scroll on route change.
  //
  // We still expose this hook so the parent layout's call site keeps
  // working (one less thing to delete + risk regressing).
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    // eslint-disable-next-line no-undef
    if (typeof window === 'undefined') return;
    try {
      // eslint-disable-next-line no-undef
      if ('scrollRestoration' in window.history) {
        // eslint-disable-next-line no-undef
        window.history.scrollRestoration = 'auto';
      }
    } catch {
      // Some browsers (privacy-locked iframes) throw on setter — swallow.
    }
  }, []);
  // No-op on path change — let the browser handle scroll restoration.
  useEffect(() => {
    // Intentional no-op. `pathname` kept in the deps array as a
    // documentation hint that this hook runs per-route.
  }, [pathname]);
}

/**
 * Friendly fallback when SQLite can't open the DB. The realistic causes on
 * web are (1) multiple tabs holding the same OPFS file, and (2) a partial
 * migration left state inconsistent. Both want "close other tabs and
 * reload" — so we say that, in plain English, instead of dumping the raw
 * NoModificationAllowedError on the user.
 */
function DbErrorScreen({ error, onRetry }: { error: Error; onRetry: () => void }) {
  const msg = error.message;
  const isInsecureContext =
    msg.includes('navigator.storage') ||
    msg.includes('context is not secure') ||
    msg.includes('not supported by your browser');
  const isOpfsConflict =
    msg.includes('NoModificationAllowedError') ||
    msg.includes('Access Handle');
  const title = isInsecureContext
    ? "This browser can't store your data here"
    : isOpfsConflict
    ? 'Bring Homestead to this tab'
    : "Couldn't open your data";
  const body = isInsecureContext
    ? "Homestead saves to your browser's secure storage, which only works on https:// or http://localhost. To use it on this phone, install Expo Go and scan the QR code from your dev server, or open the localhost URL on the same computer."
    : isOpfsConflict
    ? "Homestead is already open in another tab. Tap below and we'll move it over to this tab automatically — the other tab will gracefully refresh to a standby state."
    : 'Something went wrong reading your local database. Reload — your data is safe.';
  // Auto-takeover when this is the OPFS-lock error case: broadcast a
  // request, wait for the holder to release, then reload. The user can
  // still tap Reload manually to trigger it. On non-OPFS errors we just
  // do a plain reload.
  const handleAction = () => {
    if (Platform.OS !== 'web') {
      onRetry();
      return;
    }
    if (isOpfsConflict) {
      void requestTakeoverAndReload();
      return;
    }
    if (typeof window !== 'undefined') window.location.reload();
  };
  // Fire the takeover automatically ONLY if this tab is currently the
  // user's visible / focused tab. The visibility check is the critical
  // piece that prevents two tabs from infinite-ping-ponging the lock —
  // a tab that just yielded comes back as a hidden tab, and a hidden
  // tab should NOT auto-reclaim, it should sit in standby until the
  // user actually focuses it. The button stays as a manual fallback.
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    if (!isOpfsConflict) return;
    const teardown = scheduleTakeoverOnFocus();
    return teardown;
  }, [isOpfsConflict]);
  return (
    <View style={dbErrStyles.shell}>
      <View style={dbErrStyles.card}>
        <Text style={dbErrStyles.title}>{title}</Text>
        <Text style={dbErrStyles.body}>{body}</Text>
        <Text style={dbErrStyles.tech}>{msg}</Text>
        <Pressable
          onPress={handleAction}
          style={({ pressed }) => [dbErrStyles.btn, pressed && { opacity: 0.85 }]}>
          <Text style={dbErrStyles.btnText}>
            {isOpfsConflict ? 'Move it here' : 'Reload'}
          </Text>
        </Pressable>
      </View>
    </View>
  );
}

const dbErrStyles = StyleSheet.create({
  shell: {
    flex: 1, backgroundColor: Palette.sand,
    alignItems: 'center', justifyContent: 'center', padding: 24,
  },
  card: {
    width: '100%', maxWidth: 380,
    backgroundColor: Palette.card,
    borderWidth: 1, borderColor: Palette.line,
    borderRadius: 24, padding: 22,
  },
  title: {
    fontFamily: Fonts.serifBold, fontSize: 20, color: Palette.ink,
    letterSpacing: -0.3,
  },
  body: {
    marginTop: 8, fontFamily: Fonts.sansMedium, fontSize: 14,
    color: Palette.inkSoft, lineHeight: 21,
  },
  tech: {
    marginTop: 14, padding: 10, borderRadius: 10,
    backgroundColor: Palette.sand,
    fontFamily: Fonts.sansSemibold, fontSize: 12, color: Palette.inkFaint,
  },
  btn: {
    marginTop: 16, backgroundColor: Palette.forest,
    paddingVertical: 14, borderRadius: 14,
    alignItems: 'center',
  },
  btnText: { color: Palette.cream, fontFamily: Fonts.sansBold, fontSize: 14.5 },
});

const styles = StyleSheet.create({
  outer: {
    flex: 1,
    alignItems: 'center',
    // Slightly different from the inner sand so desktop gets a subtle
    // "phone-on-a-background" feel; on a phone-width viewport the difference
    // is invisible because the column fills the screen.
    backgroundColor: '#EDE3D0',
  },
  column: {
    flex: 1,
    width: '100%',
    maxWidth: APP_MAX_WIDTH,
    backgroundColor: Palette.sand,
  },
  overlay: {
    // Cream backdrop matches the OS splash so the underlying app — which
    // may be rendering text in fallback fonts while Fraunces is still
    // downloading — is hidden until the fonts have actually landed.
    backgroundColor: '#FBF6EA',
    zIndex: 1000,
  },
});
